import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { ConfirmationModal } from './confirmation_modal';
import { closeModal, openModal } from 'mastodon/actions/modal';
import { generateAndUploadKeypair } from 'mastodon/e2ee/keys';
import { enableConversationE2EEWithConvKey as enableConversationE2EEWithConvKeyAction } from 'mastodon/actions/conversations';

export const ConfirmEnableE2eeModal = ({ conversationId, onClose }) => {
  const dispatch = useDispatch();
  const me = useSelector(state => state.getIn(['meta', 'me']));
  const account = useSelector(state => state.getIn(['accounts', me]));
  const [loading, setLoading] = useState(false);

  const handleGenerate = useCallback(async () => {
    setLoading(true);
    try {
      await generateAndUploadKeypair(me);
      // proceed to enable and distribute
      await dispatch(enableConversationE2EEWithConvKeyAction(conversationId));
      dispatch(closeModal({ modalType: 'CONFIRM_ENABLE_E2EE', ignoreFocus: false }));
      dispatch(openModal({ modalType: 'ALERT', modalProps: { title: 'E2EE enabled', body: 'Your key was generated and the conversation is now E2EE-enabled.' } }));
    } catch (e) {
      console.error('E2EE enable error', e);
      const msg = e.message || e.toString();
      if (/authenticated|401|not authorized|access token is invalid/i.test(msg)) {
        dispatch(openModal({ modalType: 'ALERT', modalProps: { title: 'Sign-in required', body: 'Your session is not authenticated. Please sign out and sign in again (or open this site in a private window), then try enabling E2EE.' } }));
      } else {
        dispatch(openModal({ modalType: 'ALERT', modalProps: { title: 'E2EE key generation failed', body: msg } }));
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, me, conversationId]);

  const handleUpload = useCallback(() => {
    // open account settings (user can upload key there)
    dispatch(closeModal({ modalType: 'CONFIRM_ENABLE_E2EE', ignoreFocus: false }));
    // show alert instructing to go to account settings
    dispatch(openModal({ modalType: 'ALERT', modalProps: { title: 'Upload key', body: 'Please open Account settings → Security to upload your E2EE key, then enable E2EE for the conversation.' } }));
  }, [dispatch]);

  return (
    <ConfirmationModal
      title={<FormattedMessage id='conversation.confirm_enable_e2ee.title' defaultMessage='Enable end-to-end encryption?' />}
      message={<FormattedMessage id='conversation.confirm_enable_e2ee.message' defaultMessage={'You do not have an account E2EE key set up. You can generate and upload one now (recommended), or upload an existing key from account settings.'} />}
      confirm={<FormattedMessage id='conversation.generate_key' defaultMessage='Generate and upload key' />}
      secondary={<FormattedMessage id='conversation.upload_key' defaultMessage='Upload key manually' />}
      cancel={null}
      onConfirm={handleGenerate}
      onSecondary={handleUpload}
      onClose={onClose}
      closeWhenConfirm={false}
      extraContent={loading ? <p>Generating key…</p> : null}
    />
  );
};

export default ConfirmEnableE2eeModal;
